import {
  type SettingsItemProps,
  SettingsModal,
} from '@carboncopyco/components';
import {
  BrowserIcon,
  MouseActionIcon,
  SettingsIcon,
  SlideshowOutlineIcon,
} from '@carboncopyco/icons';
import type { AnimationSettings } from '@carboncopyco/presenter';
import {
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { useAnalytics } from '@carboncopyco/utils';
import {
  settingInitData as initData,
  settingsState,
} from '../../../shared/appSettings';
import { AnalyticsEvents } from '../../../shared/constants/analytics.events';
import useAsyncMemo from '../../hooks/useAsyncMemo';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import AppConfiguration from './subItems/AppConfiguration';
import AssetPresentation from './subItems/AssetPresentation';
import BrowserSettings from './subItems/BrowserSettings';
import MouseAnimation from './subItems/MouseAnimation';

interface SettingsAppModalProps {
  onCloseModal: (isConfirmed?: boolean) => void;
  divRef: React.RefObject<any>;
}

const useConfigData = () => {
  const { trackEvent } = useAnalytics();
  const items: SettingsItemProps[] = [
    {
      displayLabel: 'Caching',
      label: SettingMainItemsKeys.AppConfiguration,
      icon: <SettingsIcon size={18} />,
      Content: AppConfiguration,
      initData: {
        [SettingsSubKeys.PersistentStorageToggleState]: {
          onSaved: (value: boolean) => {
            trackEvent(AnalyticsEvents.SETTINGS_PERSISTENT_CACHING_ENABLED, {
              location: 'settings',
              checkedItem: {
                newValue: value,
              },
            });
          },
        },
        [SettingsSubKeys.TabCaching]: {
          onSaved: (value: boolean) => {
            trackEvent(AnalyticsEvents.SETTINGS_TAB_CACHING_CHANGE, {
              location: 'settings',
              checkedItem: {
                newValue: value,
              },
            });
          },
        },
      },
    },
    {
      label: SettingMainItemsKeys.MouseAnimation,
      icon: <MouseActionIcon size={18} />,
      Content: MouseAnimation,
      onSaveAll: (data: AnimationSettings) => {
        const { on, ...rest } = data;
        window.dsDesktop.demostacking.updateAnimationSettings(rest);
      },
      initData: {
        [SettingsSubKeys.MouseAnimationColor]: {
          onSaved: () => {
            trackEvent(AnalyticsEvents.SETTINGS_MOUSE_ANIMATION_COLOR_CHANGE, {
              location: 'settings',
            });
          },
        },
        [SettingsSubKeys.MouseAnimationSpeed]: {
          onSaved: () => {
            trackEvent(AnalyticsEvents.SETTINGS_MOUSE_ANIMATION_SPEED_CHANGE, {
              location: 'settings',
            });
          },
        },
        [SettingsSubKeys.MouseAnimationSize]: {
          onSaved: () => {
            trackEvent(AnalyticsEvents.SETTINGS_MOUSE_ANIMATION_SIZE_CHANGE, {
              location: 'settings',
            });
          },
        },
        [SettingsSubKeys.MouseAnimationToggleState]: {
          onSaved: () => {
            trackEvent(AnalyticsEvents.SETTINGS_MOUSE_ANIMATION_TOGGLE_CHANGE, {
              location: 'settings',
            });
          },
        },
      },
    },
    {
      label: SettingMainItemsKeys.BrowserSettings,
      icon: <BrowserIcon size={18} />,
      Content: BrowserSettings,
      initData: {
        [SettingsSubKeys.BrowserAddress]: {
          onSaved: () => {
            trackEvent(AnalyticsEvents.SETTINGS_BROWSER_HOME_PAGE_CHANGE, {
              location: 'settings',
            });
          },
        },
      },
    },
    {
      label: SettingMainItemsKeys.PresentationDisplay,
      icon: <SlideshowOutlineIcon size={18} />,
      Content: AssetPresentation,
      initData: {
        [SettingsSubKeys.ShowSpeakerPanelByDefault]: {
          onSaved: (value: boolean) => {
            window.dsDesktop.demostacking.toggleSpeakerViewScreenShare(!value);
            trackEvent(
              AnalyticsEvents.SETTINGS_PRESENTATION_DISPLAY_SPEAKER_PANEL_VISIBILITY_CHANGE,
              {
                location: 'settings',
              }
            );
          },
        },
        [SettingsSubKeys.DemoUrlDisplay]: {
          onSaved: (value: string) => {
            trackEvent(
              AnalyticsEvents.SETTINGS_PRESENTATION_DISPLAY_DOMAIN_MODE_CHANGE,
              {
                location: 'settings',
                checkedItem: {
                  mode: value,
                },
              }
            );
          },
        },
      },
    },
  ];
  return { items };
};

const SettingsAppModal = ({ onCloseModal, divRef }: SettingsAppModalProps) => {
  const flags = useFlagsFallback();
  const { data: initSettingsData, isLoading } = useAsyncMemo(async () => {
    return settingsState.getItems();
  }, []);
  const { items } = useConfigData();
  const close = () => {
    onCloseModal();
  };
  const onReset = () => initData;

  return !isLoading ? (
    <div ref={divRef}>
      <SettingsModal
        title="App Settings"
        onClose={close}
        alertOnCloseUnsaved
        open
        shouldCountChanges
        initialItemKey={SettingMainItemsKeys.AppConfiguration}
        items={[...items]}
        onReset={flags.desktopResetSettings ? onReset : undefined}
        initialData={initSettingsData || {}}
        onSubmit={settingsState.setItem}
      />
    </div>
  ) : null;
};

export default SettingsAppModal;
